import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldAlert, Loader2 } from "lucide-react";
import { adminSocialOverview } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLOR = {
  published: "text-emerald-400", failed: "text-red-400", uploading: "text-amber-400",
  pending: "text-slate-300", scheduled: "text-sky-400", reauth_required: "text-orange-400", cancelled: "text-slate-500",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") adminSocialOverview().then(setData).catch(() => setErr(true));
  }, [user]);

  if (loading) return <div className="py-24 text-center"><Loader2 className="h-7 w-7 animate-spin text-[#FF6A5E] mx-auto" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return (
    <div className="max-w-lg mx-auto py-24 text-center">
      <ShieldAlert className="h-10 w-10 text-amber-400 mx-auto" />
      <p className="mt-3 text-slate-300">Admins only.</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12" data-testid="admin-page">
      <p className="eyebrow">Admin</p>
      <h1 className="font-display font-extrabold text-3xl mt-2">Publishing monitor</h1>
      {err && <p className="text-red-400 mt-4">Failed to load.</p>}
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-[#FF6A5E] mt-8" /> : (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Connections" value={data.counts.connections} />
            {["published", "failed", "pending"].map((s) => (
              <Stat key={s} label={s} value={data.counts.jobs_by_status?.[s] || 0} />
            ))}
          </div>

          <h2 className="font-display font-bold text-lg mt-8">Platform setup</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.platforms.map((p) => (
              <span key={p.id} className={`rounded-full px-3 py-1 text-xs border ${p.configured ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                {p.name}: {p.configured ? "configured" : "setup pending"}
              </span>
            ))}
          </div>

          <h2 className="font-display font-bold text-lg mt-8">Recent publishing activity</h2>
          <div className="mt-3 rounded-2xl border border-white/10 overflow-hidden">
            {data.jobs.length === 0 && <p className="p-4 text-sm text-slate-500">No publishing jobs yet.</p>}
            {data.jobs.slice(0, 25).map((j) => (
              <div key={j.id} className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 text-sm">
                <span className="text-slate-300 truncate">{j.video_title || j.pid} · {j.platform_name}</span>
                <span className={STATUS_COLOR[j.status] || "text-slate-400"}>{j.status}</span>
              </div>
            ))}
          </div>

          <h2 className="font-display font-bold text-lg mt-8">Recent API errors</h2>
          <div className="mt-3 rounded-2xl border border-white/10 overflow-hidden">
            {data.errors.length === 0 && <p className="p-4 text-sm text-slate-500">No errors logged.</p>}
            {data.errors.slice(0, 20).map((e) => (
              <div key={e.id} className="px-4 py-2.5 border-b border-white/5 text-sm">
                <span className="text-red-400">{e.platform}</span> <span className="text-slate-400">— {e.error}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-[#11141E] p-4">
    <p className="text-2xl font-display font-extrabold text-white">{value}</p>
    <p className="text-xs text-slate-400 capitalize">{label.replace("_", " ")}</p>
  </div>
);
